import { Paper, Box, Typography, useTheme, alpha, Skeleton } from '@mui/material'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useTranslation } from '@i18n'
import { useMemo } from 'react'

const FormPerformanceChart = ({ data = [], isLoading = false }) => {
    const { t, language } = useTranslation()
    const theme = useTheme()

    // Theme-based colors
    const bgPaper = theme.palette.background.paper
    const primaryColor = theme.palette.primary.main
    const primaryLight = theme.palette.primary.light
    const dividerColor = theme.palette.divider
    const textSecondary = theme.palette.text.secondary
    const successColor = theme.palette.success.main
    const errorColor = theme.palette.error.main

    // Transform data based on language
    const chartData = useMemo(() => {
        if (!data || data.length === 0) return []
        return data.map(item => ({
            name: language === 'ar' ? (item.name_ar || item.name) : item.name,
            thisMonth: item.thisMonth,
            lastMonth: item.lastMonth
        }))
    }, [data, language])

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const thisMonth = payload.find(p => p.dataKey === 'thisMonth')?.value || 0
            const lastMonth = payload.find(p => p.dataKey === 'lastMonth')?.value || 0
            const change = lastMonth > 0 ? (((thisMonth - lastMonth) / lastMonth) * 100).toFixed(1) : 0
            const isPositive = thisMonth >= lastMonth

            return (
                <Box
                    sx={{
                        bgcolor: alpha(bgPaper, 0.95),
                        backdropFilter: 'blur(20px)',
                        p: 2,
                        borderRadius: 2,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
                        border: `1px solid ${alpha(dividerColor, 0.1)}`,
                        minWidth: 140,
                        direction: language === 'ar' ? 'rtl' : 'ltr',
                        textAlign: language === 'ar' ? 'right' : 'left'
                    }}
                >
                    <Typography variant="body2" fontWeight={700} color="text.primary" gutterBottom>{label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: primaryColor }} />
                        <Typography variant="body2">
                            {t('dashboard.thisMonth', 'This Month')}: <strong>{thisMonth}</strong>
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: primaryLight }} />
                        <Typography variant="body2">
                            {t('dashboard.lastMonth', 'Last Month')}: <strong>{lastMonth}</strong>
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            mt: 1.5,
                            pt: 1,
                            borderTop: `1px solid ${dividerColor}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: isPositive ? successColor : errorColor,
                                fontWeight: 700,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    fontSize: '1rem',
                                    lineHeight: 1
                                }}
                            >
                                {isPositive ? '↑' : '↓'}
                            </Box>
                            {Math.abs(change)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            vs last month
                        </Typography>
                    </Box>
                </Box>
            )
        }
        return null
    }

    const hasData = chartData.length > 0

    // Determine if RTL
    const isRTL = language === 'ar'

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 4,
                height: '100%',
                direction: isRTL ? 'rtl' : 'ltr',
                background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${alpha(bgPaper, 0.9)} 0%, ${alpha(primaryColor, 0.05)} 100%)`
                    : `linear-gradient(135deg, ${bgPaper} 0%, ${alpha(primaryColor, 0.02)} 100%)`,
                backdropFilter: 'blur(20px)',
                border: `1px solid ${alpha(dividerColor, 0.08)}`,
                boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    boxShadow: `0 8px 40px ${alpha(primaryColor, 0.15)}`,
                    transform: 'translateY(-2px)'
                }
            }}
        >
            <Box sx={{
                mb: 3,
                textAlign: isRTL ? 'right' : 'left'
            }}>
                <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                    {t('dashboard.formPerformance', 'Form Performance')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t('dashboard.performanceDescription', 'This month vs last month comparison')}
                </Typography>
            </Box>

            <Box sx={{ height: 260 }}>
                {isLoading ? (
                    <Skeleton variant="rectangular" height="100%" sx={{ borderRadius: 2 }} />
                ) : hasData ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            margin={language === 'ar'
                                ? { top: 5, right: 0, left: 20, bottom: 5 }
                                : { top: 5, right: 20, left: 0, bottom: 5 }
                            }
                        >
                            <defs>
                                <linearGradient id="thisMonthGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={primaryColor} stopOpacity={1} />
                                    <stop offset="100%" stopColor={primaryColor} stopOpacity={0.7} />
                                </linearGradient>
                                <linearGradient id="lastMonthGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={primaryLight} stopOpacity={0.8} />
                                    <stop offset="100%" stopColor={primaryLight} stopOpacity={0.4} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={alpha(dividerColor, 0.5)} vertical={false} />
                            <XAxis
                                dataKey="name"
                                stroke={textSecondary}
                                fontSize={9}
                                tickLine={false}
                                axisLine={false}
                                angle={-25}
                                textAnchor="end"
                                height={70}
                                interval={0}
                            />
                            <YAxis
                                stroke={textSecondary}
                                fontSize={11}
                                tickLine={false}
                                axisLine={false}
                                orientation={language === 'ar' ? 'right' : 'left'}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: alpha(primaryColor, 0.05) }} />
                            <Bar
                                dataKey="thisMonth"
                                name={t('dashboard.thisMonth', 'This Month')}
                                fill="url(#thisMonthGradient)"
                                radius={[6, 6, 0, 0]}
                                barSize={20}
                            />
                            <Bar
                                dataKey="lastMonth"
                                name={t('dashboard.lastMonth', 'Last Month')}
                                fill="url(#lastMonthGradient)"
                                radius={[6, 6, 0, 0]}
                                barSize={20}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('common.noData', 'No data available')}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Legend */}
            {hasData && !isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: 1,
                                background: `linear-gradient(180deg, ${primaryColor}, ${alpha(primaryColor, 0.7)})`,
                                boxShadow: `0 0 8px ${alpha(primaryColor, 0.4)}`
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {t('dashboard.thisMonth', 'This Month')}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: 1,
                                background: `linear-gradient(180deg, ${alpha(primaryLight, 0.8)}, ${alpha(primaryLight, 0.4)})`,
                                boxShadow: `0 0 8px ${alpha(primaryLight, 0.3)}`
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {t('dashboard.lastMonth', 'Last Month')}
                        </Typography>
                    </Box>
                </Box>
            )}
        </Paper>
    )
}

export default FormPerformanceChart
